import { useEffect, useRef, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridColumnMenuFilter,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { appStore } from '../stores/app-store';
import { ColumnMenu } from '../components/columnMenu';
import { useNavigate } from 'react-router-dom';
import { CustomFilterLanguage } from './filter/custom-filter-language';
import { Button } from '@progress/kendo-react-buttons';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import Toolbar from '../components/page-toolbar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { FeedbackCardView } from './feedback-cardview';
import { CardViewDataStateChangeEvent } from './organisations-cardview';
import YesNoDialog from '../components/dialogs/yes-no-dialog';
import { YesNoDialogResult } from '../types/dialog-result';
import {
  DeleteFeedbackAsync,
  DuplicateFeedbackAsync
} from '../services/feedback';
import { NoRecords } from '../components/grid/no-records';
import { ActionsMylibrary } from './command-grid/command-grid-mylibrary';
import { formatDateToDDMMYYYY } from '../utils/date';
import HelpSupport from '../components/help-support';
import { useToast } from '../contexts/toast-context';
import { useGetFeedbacks } from '../hooks/services/use-feedbacks';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

interface FeedbackGridProps {
  isApproval?: boolean;
}

const FeedbackGrid = ({ ...props }: FeedbackGridProps) => {
  const navigate = useNavigate();

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');
  const [selectedId, setSelectedId] = useState<number>(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  const { onToastOpen } = useToast();

  const { data, isLoading, mutate } = useGetFeedbacks(
    toDataSourceRequestString(dataState),
    props.isApproval ? 'admin' : undefined
  );

  const handleTitleClick = (id: number) => {
    if (props.isApproval) {
      navigate(`../feedback-detail-view/${id}`);
    } else {
      navigate(`../feedback-details/${id}`);
    }
  };

  const onColumnTitleClick = (props: GridCellProps) => (
    <EditCommandCell {...props} />
  );

  const EditCommandCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        <span
          className={'link-primary cursor-pointer p-0 text-start'}
          onClick={() => handleTitleClick(props.dataItem.id)}>
          {props.dataItem.name}
        </span>
      </td>
    );
  };

  const onActionRow = (props: GridCellProps) => (
    <ActionsMylibrary
      onCopy={onCopyFeedback}
      onView={(e: any) => navigate(`../feedback-detail-readonly/${e as any}`)}
      onEdit={handleTitleClick}
      onDelete={onDelete}
      {...props}
    />
  );

  const StatusName = (props: GridCellProps) => (
    <td className={'k-table-td'}>
      {(() => {
        switch (props.dataItem.status) {
          case 'New':
            return 'Draft';
          case 'Published':
          case 'Rejected':
            return 'Published';
          case 'Pending':
            return 'Published/Submitted';
          case 'Approved':
            return 'Published/Global Library';
          default:
            return '{props.dataItem.status}';
        }
      })()}
    </td>
  );

  const onAddFeedback = () => {
    navigate('../mylibrary/feedback/new');
  };

  const onViewFeedback = (id: number) => {
    // navigate('../globallibraries/' + id.toString() + '?page=feedback');
  };

  const onEditFeedback = (id: number) => {
    // navigate(`../designer/${+id.toString()}`);
  };

  const onCopyFeedback = async (id: number) => {
    try {
      appStore.showLoading();

      await DuplicateFeedbackAsync(id);

      onToastOpen({
        style: 'success',
        title: 'Feedback list',
        body: <div>Feedback copied to Feedback list.</div>
      });
    } catch (err: any) {
      onToastOpen({
        title: 'Feedback list',
        body: <div>Failed to copy to Feedback list.</div>,
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
      mutate();
    }
  };

  const onDelete = (id: number) => {
    onDeleteFeedback(id);
  };

  const onDeleteFeedback = async (id: number) => {
    setSelectedId(id);
    setShowDeleteConfirm(true);
  };

  const onConfirmDelete = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      try {
        appStore.showLoading();

        await DeleteFeedbackAsync(selectedId);
        mutate();

        onToastOpen({
          title: 'Feedback List',
          body: <div>Feedback deleted.</div>,
          style: 'success'
        });
      } catch {
        onToastOpen({
          title: 'Feedback',
          body: <div>Failed to delete feedback.</div>,
          style: 'error'
        });
      } finally {
        appStore.hideLoading();
      }
    }
    setShowDeleteConfirm(false);
  };

  const closeDeleteDialog = () => {
    setShowDeleteConfirm(false);
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  const DateCell = (props: GridCellProps, type: 'created' | 'updated') => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(
          props.dataItem[
            type === 'created' ? 'createdDateUtc' : 'modifiedDateUtc'
          ]
        )}
      </td>
    );
  };

  const CellEllipsisAndTooltip = (props: GridCellProps) => {
    return (
      <td title={props.dataItem.language} className={'k-table-td'}>
        <div className={'grid-ellipsis-cell'}>{props.dataItem.language}</div>
      </td>
    );
  };

  useEffect(() => {
    mutate();
  }, [dataState]);

  return (
    <>
      <Toolbar
        title={`${
          props.isApproval ? 'Feedback Pending Approval' : 'Feedback'
        }`}>
        {!props.isApproval && (
          <>
            <HelpSupport
              title="My feedback library guide"
              url="https://forum.catalystglobal.com/t/4162"
            />
            <Button onClick={onAddFeedback} themeColor={'primary'}>
              Add new
            </Button>
          </>
        )}
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search feedback'}
          columnsToSearch={['name']}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={`feedback.xlsx`}>
          {gridMode === 'grid' && (
            <Grid
              id={'grid-feedback'}
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              pageable={DefaultGridSettings.pagerSettings}
              sortable={true}
              data={data}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}>
              <Column
                field={'name'}
                title={'Name'}
                columnMenu={ColumnMenu}
                cells={{
                  data: (props) => onColumnTitleClick(props)
                }}
              />
              <Column
                field={'createdDateUtc'}
                title={'Created'}
                width={110}
                cells={{
                  data: (props) => DateCell(props, 'created')
                }}
              />
              <Column
                field={'createdDateUtc'}
                title={'Updated'}
                width={110}
                cells={{
                  data: (props) => DateCell(props, 'updated')
                }}
              />
              <Column
                field={'source'}
                title={'Source'}
                filter={'text'}
                width={110}
                columnMenu={ColumnMenu}
              />
              <Column
                title={'Status'}
                field={'status'}
                width={180}
                cells={{
                  data: (props) => StatusName(props)
                }}
              />
              <Column
                field={'language'}
                title={'Languages'}
                width={200}
                cells={{
                  data: (props) => CellEllipsisAndTooltip(props)
                }}
                columnMenu={(props: any) => (
                  <GridColumnMenuFilter
                    {...props}
                    filterUI={CustomFilterLanguage}
                  />
                )}
              />
              {!props.isApproval && (
                <Column
                  title={'Actions'}
                  cells={{ data: (props) => onActionRow(props) }}
                  width={210}
                />
              )}

              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}

          {gridMode === 'card' && (
            <FeedbackCardView
              data={data?.data ?? []}
              onTitlelick={handleTitleClick}
              onCopy={onCopyFeedback}
              onView={handleTitleClick}
              onEdit={onEditFeedback}
              onDelete={onDeleteFeedback}
              total={data?.total}
              {...dataState}
              onDataStateChange={(e: CardViewDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}
            />
          )}
        </ExcelExport>

        {showDeleteConfirm && (
          <YesNoDialog
            title={'Remove Feedback'}
            onConfirm={onConfirmDelete}
            onClose={closeDeleteDialog}>
            Are you sure you want to delete this feedback?
          </YesNoDialog>
        )}
      </div>
    </>
  );
};
export default FeedbackGrid;
